import { useTranslation } from "react-i18next";
import { ArrowLeft, History } from "lucide-react";
import { AccountMenu } from "@/features/account";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  SOCIETY_ENTRY_PARAM,
  SOCIETY_HISTORY_PARAM,
  SOCIETY_SIMULATION_ENTRY,
} from "@/features/society/public/presets";

export function AppShell() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const historyId = new URLSearchParams(location.search).get(SOCIETY_HISTORY_PARAM);
  const showSimulationReturn = location.pathname === "/society"
    && new URLSearchParams(location.search).get(SOCIETY_ENTRY_PARAM) === SOCIETY_SIMULATION_ENTRY;

  const toggleLanguage = () => {
    void i18n.changeLanguage(i18n.language.startsWith("zh") ? "en" : "zh");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header
        className="app-topbar fixed inset-x-0 top-0 z-50 grid h-[74px] grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b border-white/[0.12] px-6"
      >
        <NavLink
          to="/workspace"
          className="app-brand group flex h-full items-center"
          aria-label={t("app.name")}
        >
          <span
            className="app-brand-mark"
            aria-hidden="true"
          >
            <span>O</span>
          </span>
          <span className="flex items-center gap-3 font-display text-lg tracking-[-0.035em] text-white transition-opacity group-hover:opacity-80">
            <strong className="font-extrabold">OranAI</strong>
            <span className="h-4 w-px bg-white/20" aria-hidden="true" />
            <span className="font-medium text-white/58">OranSim</span>
          </span>
        </NavLink>

        <nav className="flex items-center gap-5 text-xs text-white/55">
          <AccountMenu />
          <span className="hidden md:inline">{t("topbar.help")}</span>
          <NavLink to="/history" className="hidden items-center gap-1.5 transition-colors hover:text-white md:inline-flex">
            <History className="size-3.5" aria-hidden="true" />
            {t("topbar.history")}
          </NavLink>
          <button
            type="button"
            className="transition-colors hover:text-white"
            onClick={toggleLanguage}
            aria-label={t("topbar.switchLanguage")}
          >
            {t("topbar.currentLanguage")}
          </button>
        </nav>
      </header>

      {showSimulationReturn && (
        <button
          type="button"
          data-testid="society-return-simulation"
          className="fixed right-3 top-[128px] z-40 inline-flex h-8 items-center gap-1.5 rounded-full border border-accent/30 bg-black/55 px-3 text-xs text-white/70 backdrop-blur-xl transition hover:bg-accent/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 md:right-[22px] md:top-[86px]"
          aria-label={t("society.controls.backSimulation")}
          onClick={() => historyId
            ? navigate(`/workspace?legacy_id=${encodeURIComponent(historyId)}`)
            : navigate(-1)}
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          <span>{t("society.controls.backSimulation")}</span>
        </button>
      )}

      <main className="min-h-screen pt-[74px]">
        <Outlet />
      </main>
    </div>
  );
}
